import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Composant Divider élégant et modulaire
 * 
 * @param {string} variant - Style du divider: 'default' | 'gradient' | 'dashed' | 'dotted' | 'thick'
 * @param {React.ComponentType} icon - Icône optionnelle (de lucide-react)
 * @param {string} label - Texte optionnel à afficher
 * @param {string} className - Classes CSS additionnelles
 * @param {string} iconColor - Couleur de l'icône (text-color-500)
 * @param {string} position - Position du label: 'left' | 'center' | 'right'
 * @param {string} spacing - Espacement vertical: 'sm' | 'md' | 'lg' | 'xl'
 */
const Divider = ({ 
  variant = 'default', 
  icon: Icon, 
  label, 
  className,
  iconColor = 'text-muted-foreground',
  position = 'center',
  spacing = 'md'
}) => {
  // Configuration des espacements
  const spacingClasses = {
    sm: 'my-4',
    md: 'my-6',
    lg: 'my-8',
    xl: 'my-12'
  };

  // Configuration des styles de ligne
  const lineStyles = {
    default: 'border-border',
    gradient: 'border-transparent bg-gradient-to-r from-transparent via-border to-transparent h-[2px] border-0',
    dashed: 'border-border border-dashed',
    dotted: 'border-border border-dotted',
    thick: 'border-border border-t-2'
  };

  // Configuration des positions
  const positionClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end'
  };

  // Si pas de label ni d'icône, afficher juste une ligne
  if (!label && !Icon) {
    return (
      <div className={cn(spacingClasses[spacing], className)}>
        <div className={cn('w-full border-t', lineStyles[variant])} />
      </div>
    );
  }

  // Avec label et/ou icône
  return (
    <div className={cn('flex items-center', spacingClasses[spacing], positionClasses[position], className)}>
      {/* Ligne gauche */}
      <div className={cn('flex-1 border-t', lineStyles[variant])} />
      
      {/* Contenu central */}
      <div className="flex items-center gap-2 px-4">
        {Icon && <Icon className={cn('h-4 w-4', iconColor)} />}
        {label && (
          <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
            {label}
          </span>
        )}
      </div>
      
      {/* Ligne droite */}
      <div className={cn('flex-1 border-t', lineStyles[variant])} />
    </div>
  );
};

export default Divider;
